import { useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Badge, Button, Tabs, Typography } from 'antd';
import styled from 'styled-components';
import { COLORS } from '../../../constants/color';
import { alpha } from '../../../utils/color';
import { formatDate } from '../../../utils/format';
import {
  useNotification,
  type Notification,
} from '../../../hooks/SimViewer/useNotification';
import { useVisitHistory } from '../../../hooks/SimViewer/useVisitHistory';
import { Logo } from '../../Logo';

type Guide = {
  title: string;
  description: string;
};

const SideMenuOverlay = (): ReactNode => {
  const { isNewUser } = useVisitHistory();
  const { notifications, hasNewNotifications } = useNotification();
  const { t } = useTranslation();

  const [isVisible, setIsVisible] = useState(isNewUser || hasNewNotifications);
  const [activeTab, setActiveTab] = useState(
    isNewUser ? 'startingGuide' : 'notification'
  );

  const guides: Guide[] = [
    {
      title: t('selectArea'),
      description: t('message.selectArea'),
    },
    {
      title: t('setFilters'),
      description: t('message.setFilters'),
    },
    {
      title: t('checkAnalysis'),
      description: t('message.checkAnalysis'),
    },
  ];

  if (!isVisible) {
    return null;
  }
  return (
    <Overlay>
      <Title level={3}>
        <Trans
          i18nKey="message.welcome"
          components={{
            logo: (
              <Logo
                variant="landscape"
                width="auto"
                height={24}
                style={{ margin: '4px 0' }}
              />
            ),
          }}
        />
        <br />
        {t('message.startAnalysis')}
      </Title>
      <StyledTabs
        activeKey={activeTab}
        items={[
          {
            key: 'startingGuide',
            label: t('startingGuide'),
            children: (
              <GuideSection>
                {guides.map((guide: Guide, index: number): ReactNode => (
                  <div
                    key={index}
                    style={{
                      display: 'grid',
                      columnGap: '16px',
                      gridTemplateColumns: '48px auto',
                      gridTemplateRows: '48px auto',
                      alignItems: 'center',
                      paddingRight: 8,
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: COLORS.indigo,
                        flexShrink: 0,
                        color: 'white',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        height: '48px',
                        width: '48px',
                        borderRadius: '50%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      {index + 1}
                    </div>
                    <Typography.Title
                      level={5}
                      style={{ margin: 0, color: COLORS.indigo }}
                    >
                      {guide.title}
                    </Typography.Title>
                    <p style={{ gridColumnStart: 2, margin: 0 }}>
                      {guide.description}
                    </p>
                  </div>
                ))}
              </GuideSection>
            ),
          },
          {
            key: 'notification',
            label: t('notification'),
            children: (
              <NotificationSection>
                {notifications.map((notification: Notification): ReactNode => (
                  <Release key={notification.date.toISOString()}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'start',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <time dateTime={notification.date.toISOString()}>
                        {formatDate(notification.date)}
                      </time>
                      <Badge
                        count={t(notification.type)}
                        color={
                          notification.type === 'notice'
                            ? COLORS.red
                            : notification.type === 'improvement'
                              ? COLORS.green
                              : COLORS.indigo
                        }
                        style={{ fontWeight: 600 }}
                      />
                    </div>
                    <ul>
                      {notification.changes.map((change: string): ReactNode => (
                        <li key={change}>{change}</li>
                      ))}
                    </ul>
                  </Release>
                ))}
              </NotificationSection>
            ),
          },
        ]}
        onChange={(key: string): void => {
          setActiveTab(key);
        }}
      />
      <Button
        style={{
          background: alpha(COLORS.white, 0.5),
          border: 'none',
          marginInline: 'auto',
        }}
        onClick={(): void => {
          setIsVisible(false);
        }}
      >
        {t('startWithoutArea')}
      </Button>
    </Overlay>
  );
};

const Overlay = styled.div`
  z-index: 2;
  position: absolute;
  left: 0;
  width: 100%;
  height: 100%;
  background:
    linear-gradient(
      201deg,
      ${COLORS.transparent} 75%,
      ${alpha(COLORS.aqua, 0.2)},
      ${alpha(COLORS.blue, 0.3)}
    ),
    linear-gradient(
      257deg,
      ${COLORS.transparent} 50%,
      ${alpha(COLORS.lime, 0.5)}
    ),
    rgb(249, 249, 249);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
  padding: 100px 16px 16px 16px;
`;

const Title = styled(Typography.Title)`
  && {
    color: ${COLORS.indigo};
    font-size: 24px;
    font-weight: 500;
    line-height: 32px;
    text-wrap: balance;
    margin: 4px 0;
  }
`;

const StyledTabs = styled(Tabs)`
  flex-grow: 1;
  min-height: 0;
  .ant-tabs-nav {
    margin-bottom: 0;
    &::before {
      border-bottom: 2px solid ${alpha(COLORS.lightGray, 0.2)};
    }
  }
  .ant-tabs-content {
    padding-block: 16px;
  }
  .ant-tabs-content-holder {
    min-height: 0;
    overflow-y: auto;
    scrollbar-color: rgb(65, 97, 125) transparent;
    scrollbar-width: thin;
    mask-image: linear-gradient(
      black 0px,
      black 30px,
      black calc(100% - 30px),
      transparent 100%
    );
  }
`;

const GuideSection = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  &::after {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 23px;
    height: 100%;
    z-index: -1;
    border-right: 2px dashed darkblue;
  }
`;

const NotificationSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Release = styled.div`
  time {
    color: #444444;
    font-size: 14px;
    font-weight: 700;
  }
  ul {
    margin: 4px 0 0 24px;
    padding: 0;
  }
  li {
    margin-top: 4px;
    line-height: 1.5;
  }
`;

export { SideMenuOverlay };
